import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useApiClient } from '@/shared/hooks/useApiClient';
import { useTenantContext } from '@/shared/providers/TenantProvider';
import { useAuth } from '@/shared/providers/AuthProvider';
import { createRuntimeConfigService } from '@/shared/services/runtimeConfigService';
import type { RuntimeConfig } from '@/shared/types/runtimeConfig.types';
import { setServerTimezone } from '@/shared/utils/serverTimeContext';

/**
 * Hook to fetch and access runtime configuration parameters
 *
 * This hook fetches ~200 configuration parameters from the API that control
 * feature toggles, business rules, and system settings.
 *
 * @returns Runtime configuration data, loading state, and helper functions
 */
export const useRuntimeConfig = () => {
  const apiClient = useApiClient();
  const { tenantId } = useTenantContext();
  const { isAuthenticated } = useAuth();
  const configService = createRuntimeConfigService(apiClient);
  const queryClient = useQueryClient();

  const {
    data: config,
    isLoading,
    error,
    refetch,
  } = useQuery<RuntimeConfig>({
    queryKey: ['runtime-config', tenantId],
    queryFn: () => configService.fetchConfiguration(),
    enabled: isAuthenticated, // Only fetch after authentication
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });

  // Invalidate and refetch config when authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      // When user logs in, invalidate cache and refetch
      queryClient.invalidateQueries({ queryKey: ['runtime-config', tenantId] });
    } else {
      // When user logs out, clear the cache
      queryClient.removeQueries({ queryKey: ['runtime-config', tenantId] });
    }
  }, [isAuthenticated, tenantId, queryClient]);

  useEffect(() => {
    if (config) {
      const timezone = config['general.timezone'];
      if (timezone) {
        setServerTimezone(String(timezone));
      }
    }
  }, [config]);

  /**
   * Gets a configuration value by key with optional default
   */
  const getConfigValue = <T = string | number | boolean>(
    key: string,
    defaultValue?: T
  ): T | undefined => {
    return configService.getConfigValue<T>(config || null, key, defaultValue);
  };

  /**
   * Checks if a feature is enabled
   */
  const isFeatureEnabled = (featureKey: string): boolean => {
    return configService.isFeatureEnabled(config || null, featureKey);
  };

  return {
    config,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refetch,
    // Helper functions
    getConfigValue,
    isFeatureEnabled,
  };
};
